/**
 * ZAPHUB 2.1 — AnomalyDetectionWidget
 *
 * Polls /api/admin/anomalies and displays alerts for workflows
 * that are running slower than normal or have high failure rates.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Loader2,
  TrendingDown,
  XCircle,
  ChevronRight,
} from 'lucide-react';
import api from '../services/apiClient';
import { useNavigate } from 'react-router-dom';

interface Anomaly {
  workflowId: string;
  workflowName: string;
  type: 'slow_execution' | 'high_failure_rate' | 'no_executions';
  severity: 'warning' | 'critical';
  details: string;
  avgDurationMs?: number;
  latestDurationMs?: number;
  failureRate?: number;
}

interface AnomalyDetectionWidgetProps {
  autoRefreshSecs?: number;
  showMax?: number;
}

export function AnomalyDetectionWidget({
  autoRefreshSecs = 120,
  showMax = 8,
}: AnomalyDetectionWidgetProps) {
  const navigate = useNavigate();
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [scanned, setScanned] = useState(0);

  const check = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/anomalies');
      setAnomalies(data.data.anomalies || []);
      setScanned(data.data.scannedWorkflows || 0);
      setLastChecked(new Date());
    } catch {
      // Permission error (non-admin) — silently skip
      setAnomalies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    check();
    if (autoRefreshSecs > 0) {
      const id = setInterval(check, autoRefreshSecs * 1000);
      return () => clearInterval(id);
    }
  }, [check, autoRefreshSecs]);

  const criticals = anomalies.filter(a => a.severity === 'critical');
  const warnings = anomalies.filter(a => a.severity === 'warning');

  return (
    <Card className="overflow-hidden">
      <div className={`p-4 border-b flex items-center justify-between ${
        criticals.length > 0 ? 'bg-red-50 border-red-200' :
        warnings.length > 0 ? 'bg-yellow-50 border-yellow-200' :
        'bg-green-50 border-green-200'
      }`}>
        <div className="flex items-center gap-2">
          {loading ? (
            <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
          ) : criticals.length > 0 ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : warnings.length > 0 ? (
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-600" />
          )}
          <h3 className="font-semibold text-gray-900">Anomaly Detection</h3>
          {anomalies.length > 0 && (
            <Badge variant={criticals.length > 0 ? 'destructive' : 'default'} className="text-xs">
              {anomalies.length} alerta{anomalies.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastChecked && (
            <span className="text-xs text-gray-400">
              {lastChecked.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button variant="ghost" size="icon" onClick={check} disabled={loading} className="h-7 w-7">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {anomalies.length === 0 && !loading && (
        <div className="p-6 text-center text-gray-400">
          <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
          <p className="text-sm font-medium text-green-700">Todo funciona con normalidad</p>
          <p className="text-xs text-gray-400 mt-1">{scanned} workflows escaneados</p>
        </div>
      )}

      {anomalies.slice(0, showMax).map(a => (
        <div
          key={`${a.workflowId}-${a.type}`}
          className={`flex items-start gap-3 px-4 py-3 border-b last:border-0 cursor-pointer hover:bg-gray-50 ${
            a.severity === 'critical' ? 'bg-red-50/50' : 'bg-yellow-50/30'
          }`}
          onClick={() => navigate(`/dashboard/workflows/${a.workflowId}`)}
        >
          <div className="flex-shrink-0 mt-0.5">
            {a.severity === 'critical' ? (
              <XCircle className="w-4 h-4 text-red-600" />
            ) : (
              <TrendingDown className="w-4 h-4 text-yellow-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">{a.workflowName}</span>
              <Badge
                variant={a.severity === 'critical' ? 'destructive' : 'secondary'}
                className="text-xs flex-shrink-0"
              >
                {a.severity}
              </Badge>
            </div>
            <p className="text-xs text-gray-600 mt-0.5">{a.details}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        </div>
      ))}

      {anomalies.length > showMax && (
        <div className="px-4 py-2 text-center text-xs text-gray-400">
          +{anomalies.length - showMax} alertas más
        </div>
      )}
    </Card>
  );
}
